import { useState, useEffect, useCallback, Fragment } from 'react';
import { oncallLeadsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  Loader2, Mail, Phone, MessageCircle, Sparkles, Trash2, CheckCircle2,
  ExternalLink, Search, FileText, UserCircle2, Send, RefreshCw,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const FILTER_TABS = [
  { id: 'all', label: 'All' },
  { id: 'demo', label: 'Wants Demo' },
  { id: 'pending', label: 'Pending' },
  { id: 'handled', label: 'Handled' },
];

function StatBadge({ icon: Icon, label, value, color = 'amber' }) {
  const palette = {
    amber: 'bg-amber-500/10 border-amber-500/20 text-amber-300',
    emerald: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-300',
    blue: 'bg-blue-500/10 border-blue-500/20 text-blue-300',
    slate: 'bg-slate-500/10 border-slate-500/20 text-slate-300',
  }[color];
  return (
    <div className={`rounded-xl border px-4 py-3 ${palette}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider opacity-80">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}

function CapturedField({ icon: Icon, value, copyLabel }) {
  if (!value) return <span className="text-slate-600 text-xs">—</span>;
  const copy = () => {
    navigator.clipboard.writeText(value);
    toast.success(`${copyLabel} copied`);
  };
  return (
    <button
      onClick={copy}
      className="inline-flex items-center gap-1.5 text-xs text-slate-200 hover:text-amber-300 transition-colors group"
      title={`Click to copy ${copyLabel}`}
    >
      <Icon className="w-3.5 h-3.5 opacity-70 group-hover:opacity-100" />
      <span className="font-mono">{value}</span>
    </button>
  );
}

export default function OnCallLeadsPage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(null); // lead_id currently being acted on
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await oncallLeadsAPI.list();
      setLeads(res.data || []);
    } catch {
      toast.error('Failed to load leads');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleMarkHandled = async (lead) => {
    setBusy(lead.id);
    try {
      await oncallLeadsAPI.update(lead.id, { handled: !lead.handled });
      setLeads(prev => prev.map(l => l.id === lead.id ? { ...l, handled: !lead.handled } : l));
      toast.success(lead.handled ? 'Marked as pending' : 'Marked as handled');
    } catch {
      toast.error('Failed to update lead');
    }
    setBusy(null);
  };

  const handleDelete = async (lead) => {
    if (!confirm('Delete this lead? This cannot be undone.')) return;
    setBusy(lead.id);
    try {
      await oncallLeadsAPI.delete(lead.id);
      setLeads(prev => prev.filter(l => l.id !== lead.id));
      toast.success('Lead deleted');
    } catch {
      toast.error('Failed to delete');
    }
    setBusy(null);
  };

  const handleSendWhatsapp = async (lead) => {
    setBusy(lead.id);
    try {
      await oncallLeadsAPI.sendWhatsappDemo(lead.id);
      toast.success('WhatsApp demo sent');
      load();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to send WhatsApp demo';
      toast.error(msg, { duration: 5000 });
    }
    setBusy(null);
  };

  // Filtering
  const filtered = leads.filter(l => {
    if (filter === 'demo' && !l.wants_demo) return false;
    if (filter === 'pending' && l.handled) return false;
    if (filter === 'handled' && !l.handled) return false;
    if (search) {
      const q = search.toLowerCase();
      const blob = `${l.captured_email || ''} ${l.captured_phone || ''} ${l.captured_whatsapp || ''} ${l.caller_phone || ''} ${l.contact_name || ''} ${l.agent_name || ''} ${l.transcript_snippet || ''}`.toLowerCase();
      if (!blob.includes(q)) return false;
    }
    return true;
  });

  const stats = {
    total: leads.length,
    wantsDemo: leads.filter(l => l.wants_demo).length,
    pending: leads.filter(l => !l.handled).length,
    handled: leads.filter(l => l.handled).length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl" data-testid="oncall-leads-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit'] text-white">On-Call Leads</h1>
          <p className="text-slate-400 mt-1">
            Contact details captured automatically from live AI phone-call conversations
          </p>
        </div>
        <Button
          variant="outline"
          onClick={load}
          className="border-white/10 text-slate-300 hover:text-white hover:bg-white/5"
          data-testid="oncall-refresh-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="oncall-stats">
        <StatBadge icon={UserCircle2} label="Total leads" value={stats.total} color="slate" />
        <StatBadge icon={Sparkles} label="Wants demo" value={stats.wantsDemo} color="amber" />
        <StatBadge icon={FileText} label="Pending" value={stats.pending} color="blue" />
        <StatBadge icon={CheckCircle2} label="Handled" value={stats.handled} color="emerald" />
      </div>

      {/* Filters + search */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1 bg-slate-900/50 rounded-xl p-1 border border-white/5" data-testid="oncall-filter-tabs">
          {FILTER_TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setFilter(t.id)}
              data-testid={`oncall-filter-${t.id}`}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                filter === t.id
                  ? 'bg-amber-600/15 text-amber-300'
                  : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search email / phone / agent / transcript..."
            data-testid="oncall-search"
            className="pl-9 bg-slate-950 border-white/10 text-white placeholder:text-slate-600"
          />
        </div>
      </div>

      {/* Empty state */}
      {filtered.length === 0 && (
        <div className="glass-card rounded-xl p-12 text-center" data-testid="oncall-empty-state">
          <UserCircle2 className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold font-['Outfit'] text-white mb-2">
            {leads.length === 0 ? 'No on-call leads yet' : 'No leads match this filter'}
          </h3>
          <p className="text-slate-400 text-sm max-w-md mx-auto">
            {leads.length === 0
              ? 'Leads will appear here automatically when callers share their email, phone, or WhatsApp number during an AI call — or ask for a demo.'
              : 'Try changing the filter or clearing the search.'}
          </p>
        </div>
      )}

      {/* Leads table */}
      {filtered.length > 0 && (
        <div className="rounded-xl border border-white/10 bg-slate-900/50 overflow-hidden" data-testid="oncall-leads-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/5 bg-slate-950/40">
                <th className="px-4 py-3 text-xs text-slate-400 font-medium text-left">Date</th>
                <th className="px-4 py-3 text-xs text-slate-400 font-medium text-left">Caller</th>
                <th className="px-4 py-3 text-xs text-slate-400 font-medium text-left">Captured</th>
                <th className="px-4 py-3 text-xs text-slate-400 font-medium text-left">Intent</th>
                <th className="px-4 py-3 text-xs text-slate-400 font-medium text-left">Status</th>
                <th className="px-4 py-3 text-xs text-slate-400 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filtered.map(lead => {
                const dt = lead.created_at ? new Date(lead.created_at) : null;
                const isExpanded = expanded === lead.id;
                return (
                  <Fragment key={lead.id}>
                    <tr
                      className="hover:bg-white/[0.02] cursor-pointer"
                      onClick={() => setExpanded(isExpanded ? null : lead.id)}
                      data-testid={`oncall-row-${lead.id}`}
                    >
                      <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">
                        {dt ? dt.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
                      </td>
                      <td className="px-4 py-3 text-white text-xs">
                        <div className="font-medium">{lead.contact_name || 'Unknown'}</div>
                        <div className="text-slate-500 font-mono">{lead.caller_phone || '—'}</div>
                        <div className="text-slate-600 text-[10px] mt-0.5">via {lead.agent_name || 'AI Agent'}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          <CapturedField icon={Phone} value={lead.captured_phone} copyLabel="Phone" />
                          <CapturedField icon={Mail} value={lead.captured_email} copyLabel="Email" />
                          <CapturedField icon={MessageCircle} value={lead.captured_whatsapp} copyLabel="WhatsApp" />
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {lead.wants_demo && (
                            <Badge className="bg-amber-600/20 text-amber-300 border-amber-600/30 text-[10px]" data-testid={`oncall-demo-badge-${lead.id}`}>
                              <Sparkles className="w-3 h-3 mr-1" /> Wants Demo
                            </Badge>
                          )}
                          {(lead.intents || []).filter(i => !['demo'].includes(i)).map(i => (
                            <Badge key={i} className="bg-slate-700/40 text-slate-300 border-slate-600/40 text-[10px] capitalize">
                              {i}
                            </Badge>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {lead.handled ? (
                          <Badge className="bg-emerald-600/20 text-emerald-300 border-emerald-600/30 text-[10px]">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Handled
                          </Badge>
                        ) : (
                          <Badge className="bg-blue-600/20 text-blue-300 border-blue-600/30 text-[10px]">
                            Pending
                          </Badge>
                        )}
                        {lead.whatsapp_sent && (
                          <Badge className="bg-green-600/20 text-green-300 border-green-600/30 text-[10px] mt-1">
                            <Send className="w-3 h-3 mr-1" /> WA sent
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          {(lead.captured_whatsapp || lead.captured_phone || lead.caller_phone) && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleSendWhatsapp(lead)}
                              disabled={busy === lead.id}
                              title="Send WhatsApp Demo"
                              data-testid={`oncall-send-wa-${lead.id}`}
                              className="h-8 px-2 text-green-400 hover:text-green-300 hover:bg-green-500/10"
                            >
                              {busy === lead.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleMarkHandled(lead)}
                            disabled={busy === lead.id}
                            title={lead.handled ? 'Mark pending' : 'Mark handled'}
                            data-testid={`oncall-handle-${lead.id}`}
                            className={`h-8 px-2 ${lead.handled ? 'text-blue-400 hover:text-blue-300' : 'text-emerald-400 hover:text-emerald-300'} hover:bg-white/5`}
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </Button>
                          {lead.call_id && (
                            <Link
                              to={`/dashboard/calls?id=${lead.call_id}`}
                              onClick={(e) => e.stopPropagation()}
                              title="View call"
                              data-testid={`oncall-view-call-${lead.id}`}
                              className="h-8 px-2 inline-flex items-center text-slate-400 hover:text-white hover:bg-white/5 rounded-md"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Link>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(lead)}
                            disabled={busy === lead.id}
                            title="Delete lead"
                            data-testid={`oncall-delete-${lead.id}`}
                            className="h-8 px-2 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && lead.transcript_snippet && (
                      <tr key={`${lead.id}-snippet`} className="bg-slate-950/40">
                        <td colSpan={6} className="px-4 py-3" data-testid={`oncall-snippet-${lead.id}`}>
                          <div className="text-xs text-slate-400 mb-1 font-medium uppercase tracking-wider">
                            Transcript snippet
                          </div>
                          <pre className="text-xs text-slate-300 whitespace-pre-wrap font-mono bg-slate-950/60 p-3 rounded-lg border border-white/5 max-h-48 overflow-y-auto">
                            {lead.transcript_snippet}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
